import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { AppState } from '../app.state';
import { catchError, concat, map, mergeMap, of, share, switchMap, withLatestFrom } from 'rxjs';
import * as SharedActions from '@states/shared/shared.actions';
import { IntegrationsService } from '../../development/integrations.service';
import { IntegrationsActions } from '@states/integrations/integrations.action-types';
import { OrganizationSelectors } from '@states/organization/organization.selector-types';
import { SharedSelectors } from '@states/shared/shared.selector-types';

@Injectable()
export class IntegrationsEffect {
  public getIntegrations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.getIntegrations),
      withLatestFrom(this.store$.pipe(select(state => state.organizationState))),
      switchMap(([, { activeOrganization }]) => {

        return this.integrationsService
          .getIntegrations(activeOrganization.orgId)
          .pipe(
            switchMap(res => {
              return [
                IntegrationsActions.getIntegrationsSuccess({ integrations: res }),
              ];
            }),
            catchError(err => [SharedActions.showMessage({ error: JSON.stringify(err) })]),
          );
      }),
      share(),
    ),
  );

  public getIntegrationDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.getIntegrationDetails),
      withLatestFrom(this.store$.pipe(select(OrganizationSelectors.selectOrgId))),
      switchMap(([, orgId]) => {
        return this.integrationsService
          .getIntegrationsDetails(orgId)
          .pipe(
            map(res => IntegrationsActions.getIntegrationDetailsSuccess({ details: res })),
            catchError(err => of(SharedActions.showMessage({ error: JSON.stringify(err) }))),
          );
      }),
    ),
  );

  public updateIntegrationsQuota$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.updateIntegrationsQuota),
      mergeMap(({ noonLightQuota, lumanaQuota,vccQuota,vccSpecialFilterQuota }) => {
        return concat(
          of(SharedActions.setIsSaving({ isSaving: true })),
          this.integrationsService
            .updateIntegrationsQuota(noonLightQuota, lumanaQuota,vccQuota,vccSpecialFilterQuota)
            .pipe(
              mergeMap(res => [
                IntegrationsActions.updateIntegrationsQuotaSuccess({ details: res }),
                SharedActions.showMessage({
                  success: 'Organization quotas has been updated',
                }),
                SharedActions.setIsSaving({ isSaving: false }),
              ]),
              catchError(err => {
                console.error('API error:', err);
                return of(
                  SharedActions.showMessage({ error: JSON.stringify(err) }),
                  SharedActions.setIsSaving({ isSaving: false }),
                );
              }),
            ),
        );
      }),
    ),
  );


  public deactivateSlack$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.deactivateSlack),
      withLatestFrom(this.store$.pipe(select(state => state.organizationState))),
      switchMap(([, { activeOrganization }]) => {

        return this.integrationsService
          .deactivateSlack(activeOrganization.orgId)
          .pipe(
            switchMap(res => {
              return [
                IntegrationsActions.getIntegrations(),
                SharedActions.showMessage({ success: 'Slack uninstalled successfully' }),
              ];
            }),
            catchError(err => [SharedActions.showMessage({ error: err })]),
          );
      }),
      share(),
    ),
  );

  public uninstallTeam3$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.uninstallTeam3),
      withLatestFrom(this.store$.pipe(select(state => state.organizationState))),
      switchMap(([, { activeOrganization }]) => {

        return this.integrationsService
          .uninstallTeam3(activeOrganization.orgId)
          .pipe(
            switchMap(res => {
              return [
                IntegrationsActions.getIntegrations(),
                SharedActions.showMessage({ success: 'Team3 uninstalled successfully' }),
              ];
            }),
            catchError(err => [SharedActions.showMessage({ error: err })]),
          );
      }),
      share(),
    ),
  );

  public installTeam3$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.installTeam3),
      withLatestFrom(this.store$.pipe(select(state => state.organizationState))),
      switchMap(([, { activeOrganization }]) => {

        return this.integrationsService
          .installTeam3(activeOrganization.orgId)
          .pipe(
            switchMap(res => {
              return [
                IntegrationsActions.getIntegrations(),
                SharedActions.showMessage({ success: 'Team3 installed successfully' }),
              ];
            }),
            catchError(err => [SharedActions.showMessage({ error: err })]),
          );
      }),
      share(),
    ),
  );

  public uninstallLenelS2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.uninstallLenelS2),
      withLatestFrom(this.store$.pipe(select(state => state.organizationState))),
      switchMap(([, { activeOrganization }]) => {
        return this.integrationsService
          .uninstallLenelS2(activeOrganization.orgId)
          .pipe(
            switchMap(() => {
              return [
                IntegrationsActions.getIntegrations(),
                SharedActions.showMessage({ success: 'LenelS2 uninstalled successfully' }),
              ];
            }),
            catchError(err => [SharedActions.showMessage({ error: err })]),
          );
      }),
      share(),
    ),
  );

  public installLenelS2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.installLenelS2),
      withLatestFrom(this.store$.pipe(select(state => state.organizationState))),
      switchMap(([, { activeOrganization }]) => {
        return this.integrationsService
          .installLenelS2(activeOrganization.orgId)
          .pipe(
            switchMap(() => {
              return [
                IntegrationsActions.getIntegrations(),
                SharedActions.showMessage({ success: 'LenelS2 installed successfully' }),
              ];
            }),
            catchError(err => [SharedActions.showMessage({ error: err })]),
          );
      }),
      share(),
    ),
  );

  public installNoonLight$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.installNoonLight),
      switchMap((action) => {
        return this.integrationsService
          .installNoonLight({ token: action.token, quota: action.quota })
          .pipe(
            switchMap(res => {
              return [
                IntegrationsActions.getIntegrations(),
                SharedActions.showMessage({ success: 'NoonLight installed successfully' }),
              ];
            }),
            catchError(err => [SharedActions.showMessage({ error: err })]),
          );
      }),
      share(),
    ),
  );

  public installLumana$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.installLumana),
      switchMap((action) => {
        return this.integrationsService
          .installLumana({ quota: action.quota })
          .pipe(
            switchMap(res => {
              return [
                IntegrationsActions.getIntegrations(),
                SharedActions.showMessage({ success: 'Lumana installed successfully' }),
              ];
            }),
            catchError(err => [SharedActions.showMessage({ error: err })]),
          );
      }),
      share(),
    ),
  );

  public uninstallNoonLight$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.uninstallNoonLight),
      withLatestFrom(this.store$.pipe(select(state => state.organizationState))),
      switchMap(([, { activeOrganization }]) => {
        return this.integrationsService
          .uninstallNoonLight()
          .pipe(
            switchMap(res => {
              return [
                IntegrationsActions.getIntegrations(),
                SharedActions.showMessage({ success: 'NoonLight uninstalled successfully' }),
              ];
            }),
            catchError(err => [SharedActions.showMessage({ error: err })]),
          );
      }),
      share(),
    ),
  );


  public uninstallLumana$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.uninstallLumana),
      withLatestFrom(this.store$.pipe(select(state => state.organizationState))),
      switchMap(([, { activeOrganization }]) => {
        return this.integrationsService
          .uninstallLumana()
          .pipe(
            switchMap(res => {
              return [
                IntegrationsActions.getIntegrations(),
                SharedActions.showMessage({ success: 'Lumana uninstalled successfully' }),
              ];
            }),
            catchError(err => [SharedActions.showMessage({ error: JSON.parse(err) })]),
          );
      }),
      share(),
    ),
  );

  public uninstallGenea$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.uninstallGenea),
      withLatestFrom(this.store$.pipe(select(state => state.organizationState))),
      switchMap(([, { activeOrganization }]) => {

        return this.integrationsService
          .uninstallGenea(activeOrganization.orgId)
          .pipe(
            switchMap(res => {
              return [
                IntegrationsActions.getIntegrations(),
                SharedActions.showMessage({ success: 'Genea uninstalled successfully' }),
                IntegrationsActions.uninstallGeneaSuccess(),
              ];
            }),
            catchError(err => [SharedActions.showMessage({ error: err })]),
          );
      }),
      share(),
    ),
  );

  public installGenea$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.installGenea),
      withLatestFrom(this.store$.pipe(select(state => state.organizationState))),
      switchMap(([{ apiKey }, { activeOrganization }]) => {

        return this.integrationsService
          .installGenea(activeOrganization.orgId, apiKey)
          .pipe(
            switchMap(res => {
              return [
                IntegrationsActions.getIntegrations(),
                SharedActions.showMessage({ success: 'Genea installed successfully' }),
              ];
            }),
            catchError(err => [SharedActions.showMessage({ error: 'Error: Invalid API Key' })]),
          );
      }),
      share(),
    ),
  );

  public uninstallKisi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.uninstallKisi),
      withLatestFrom(this.store$.pipe(select(state => state.organizationState))),
      switchMap(([, { activeOrganization }]) => {

        return this.integrationsService
          .uninstallKisi(activeOrganization.orgId)
          .pipe(
            switchMap(res => {
              return [
                IntegrationsActions.getIntegrations(),
                SharedActions.showMessage({ success: 'Kisi uninstalled successfully' }),
                IntegrationsActions.uninstallKisiSuccess(),
              ];
            }),
            catchError(err => [SharedActions.showMessage({ error: err })]),
          );
      }),
      share(),
    ),
  );

  public installKisi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.installKisi),
      withLatestFrom(this.store$.pipe(select(state => state.organizationState))),
      switchMap(([{ apiKey }, { activeOrganization }]) => {

        return this.integrationsService
          .installKisi(activeOrganization.orgId, apiKey)
          .pipe(
            switchMap(res => {
              return [
                IntegrationsActions.getIntegrations(),
                SharedActions.showMessage({ success: 'Kisi installed successfully' }),
              ];
            }),
            catchError(err => [SharedActions.showMessage({ error: 'Error: Invalid API key' })]),
          );
      }),
      share(),
    ),
  );
  installImmix$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.installImmix),
      withLatestFrom(this.store$.pipe(select(state => state.organizationState))),
      switchMap(([{ payload }, { activeOrganization }]) =>
        this.integrationsService.installImmix(activeOrganization.orgId, payload.host, payload.port).pipe(
          switchMap(() => [
            IntegrationsActions.getIntegrations(),
            SharedActions.showMessage({ success: 'Immix installed successfully' }),
          ]),
          catchError((error) => of(SharedActions.showMessage({ error: error.message || 'Failed to install Immix' }))),
        ),
      ),
    ),
  );

  uninstallImmix$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.uninstallImmix),
      withLatestFrom(this.store$.pipe(select(state => state.organizationState))),
      switchMap(([_, { activeOrganization }]) =>
        this.integrationsService.uninstallImmix(activeOrganization.orgId).pipe(
          switchMap(() => [
            IntegrationsActions.uninstallImmixSuccess(),
            IntegrationsActions.getIntegrations(),
            SharedActions.showMessage({ success: 'Immix uninstalled successfully' }),
          ]),
          catchError((error) => of(SharedActions.showMessage({ error: error.message || 'Failed to uninstall Immix' }))),
        ),
      ),
    ),
  );

  installFlir$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.installFlir),
      withLatestFrom(this.store$.pipe(select(state => state.organizationState))),
      switchMap(([{ payload }, { activeOrganization }]) =>
        this.integrationsService.installFlir(activeOrganization.orgId, payload.host, payload.port).pipe(
          switchMap(() => [
            IntegrationsActions.getIntegrations(),
            SharedActions.showMessage({ success: 'FLIR installed successfully' }),
          ]),
          catchError((error) => of(SharedActions.showMessage({ error: error.message || 'Failed to install FLIR' }))),
        ),
      ),
    ),
  );

  uninstallFlir$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.uninstallFlir),
      withLatestFrom(this.store$.pipe(select(state => state.organizationState))),
      switchMap(([_, { activeOrganization }]) =>
        this.integrationsService.uninstallFlir(activeOrganization.orgId).pipe(
          switchMap(() => [
            IntegrationsActions.uninstallFlirSuccess(),
            IntegrationsActions.getIntegrations(),
            SharedActions.showMessage({ success: 'FLIR uninstalled successfully' }),
          ]),
          catchError((error) => of(SharedActions.showMessage({ error: error.message || 'Failed to uninstall FLIR' }))),
        ),
      ),
    ),
  );

  installDisruptive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.installDisruptive),
      withLatestFrom(this.store$.pipe(select(state => state.organizationState))),
      switchMap(([{ keyId, secret, projectId }, { activeOrganization }]) => {
        const orgId = activeOrganization.orgId;
        return this.integrationsService.installDisruptive(orgId, keyId, secret, projectId).pipe(
          map(() => IntegrationsActions.getIntegrations()),
          catchError(err => [SharedActions.showMessage({ error: 'Error: Invalid API key or secret' })]),
        );
      })
    )
  );

  uninstallDisruptive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.uninstallDisruptive),
      withLatestFrom(this.store$.pipe(select(state => state.organizationState))),
      switchMap(([, { activeOrganization }]) => {
        return this.integrationsService
          .uninstallDisruptive(activeOrganization.orgId)
          .pipe(
            switchMap(res => {
              return [
                IntegrationsActions.getIntegrations(),
                SharedActions.showMessage({ success: 'Disruptive uninstalled successfully' }),
              ];
            }),
            catchError(err => [SharedActions.showMessage({ error: err })]),
          );
      }),
    ),
  );

  public uninstallBrivo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.uninstallBrivo),
      withLatestFrom(this.store$.pipe(select(state => state.organizationState))),
      switchMap(([, { activeOrganization }]) => {
        return this.integrationsService
          .uninstallBrivo(activeOrganization.orgId)
          .pipe(
            switchMap(res => {
              return [
                IntegrationsActions.getIntegrations(),
                SharedActions.showMessage({ success: 'Brivo uninstalled successfully' }),
              ];
            }),
            catchError(err => [SharedActions.showMessage({ error: err })]),
          );
      }),
      share(),
    ),
  );

  public installBrivo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.installBrivo),
      withLatestFrom(this.store$.pipe(select(state => state.organizationState))),
      switchMap(([action, { activeOrganization }]) => {
        return this.integrationsService
          .installBrivo(activeOrganization.orgId, action.clientId, action.clientSecret, action.deploymentZone)
          .pipe(
            switchMap((response: any) => {
              // Open a new window for Brivo login using the authUrl from response
              if (response && response.url) {

                // const popup = window.open(response.url, '_blank', 'width=500,height=300');
                const popup = window.open(response.url, '_blank');
                const onMessage = (event: MessageEvent) => {
                  const expectedOrigins = ['https://api-dev.lumana.ai', 'https://api-staging.lumana.ai', 'https://api.lumana.ai', 'http://localhost:3000'];
                  if (!expectedOrigins.includes(event.origin)) { return; }

                  if (event.data?.type === 'BRIVO_AUTH_OK') {
                    window.removeEventListener('message', onMessage);
                    popup.close();
                    this.store$.dispatch(IntegrationsActions.getIntegrations());
                  }
                };

                window.addEventListener('message', onMessage);
                setTimeout(() => {
                  // Close the popup if it is still open after 2 minutes
                  if (popup && !popup.closed) {
                    popup.close();
                    window.removeEventListener('message', onMessage);
                    this.store$.dispatch(IntegrationsActions.getIntegrations());
                    // this.store$.dispatch(SharedActions.showMessage({ error: 'Brivo authentication timed out' }));
                  }
                }, 120000)
              }
              return [
                // IntegrationsActions.getIntegrations(),
                // SharedActions.showMessage({ success: 'Brivo installation initiated successfully' }),
              ];
            }),
            catchError(err => [SharedActions.showMessage({ error: err.error?.message || 'Failed to initiate Brivo installation' })]),
          );
      }),
      share(),
    ),
  );

  public installSchneider$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.installSchneider),
      withLatestFrom(this.store$.pipe(select(state => state.organizationState))),
      switchMap(([action, { activeOrganization }]) => {
        return this.integrationsService
          .installSchneider(
            activeOrganization.orgId,
            action.account,
            action.username,
            action.password,
            action.region,
          )
          .pipe(
            switchMap(res => {
              return [
                IntegrationsActions.getIntegrations(),
                SharedActions.showMessage({ success: 'Schneider installed successfully' }),
              ];
            }),
            catchError(err => [SharedActions.showMessage({ error: err.error?.message || 'Failed to install Schneider' })]),
          );
      }),
      share(),
    ),
  );

  public uninstallSchneider$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.uninstallSchneider),
      withLatestFrom(this.store$.pipe(select(state => state.organizationState))),
      switchMap(([, { activeOrganization }]) => {
        return this.integrationsService
          .uninstallSchneider(activeOrganization.orgId)
          .pipe(
            switchMap(res => {
              return [
                IntegrationsActions.getIntegrations(),
                SharedActions.showMessage({ success: 'Schneider uninstalled successfully' }),
                IntegrationsActions.uninstallSchneiderSuccess(),
              ];
            }),
            catchError(err => [SharedActions.showMessage({ error: err })]),
          );
      }),
      share(),
    ),
  );

  public installAccessControlBridge$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.installAccessControlBridge),
      switchMap(action => {
        return this.integrationsService
          .installAccessControlBridge(action.params)
          .pipe(
            switchMap(result => {
              return [
                IntegrationsActions.installAccessControlBridgeSuccess({ result }),
                IntegrationsActions.getIntegrations(),
                SharedActions.showMessage({ success: 'Access Control over SNS installed successfully' }),
              ];
            }),
            catchError(err => [
              SharedActions.showMessage({
                error: err.error?.message || 'Failed to install Access Control over SNS',
              }),
            ]),
          );
      }),
      share(),
    ),
  );

  public updateAccessControlBridge$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.updateAccessControlBridge),
      switchMap(action => {
        return this.integrationsService
          .updateAccessControlBridge(action.params)
          .pipe(
            switchMap(result => {
              return [
                IntegrationsActions.updateAccessControlBridgeSuccess({ result }),
                IntegrationsActions.getIntegrationDetails(),
                SharedActions.showMessage({ success: 'Connection settings saved' }),
              ];
            }),
            catchError(err => [
              SharedActions.showMessage({
                error: err.error?.message || 'Failed to save connection settings',
              }),
            ]),
          );
      }),
      share(),
    ),
  );

  public uninstallAccessControlBridge$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.uninstallAccessControlBridge),
      switchMap(() => {
        return this.integrationsService
          .uninstallAccessControlBridge()
          .pipe(
            switchMap(() => {
              return [
                IntegrationsActions.uninstallAccessControlBridgeSuccess(),
                IntegrationsActions.getIntegrations(),
                SharedActions.showMessage({ success: 'Access Control over SNS uninstalled successfully' }),
              ];
            }),
            catchError(err => [
              SharedActions.showMessage({
                error: err.error?.message || 'Failed to uninstall Access Control over SNS',
              }),
            ]),
          );
      }),
      share(),
    ),
  );

  public installGenetec$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.installGenetec),
      withLatestFrom(this.store$.pipe(select(state => state.organizationState))),
      switchMap(([{ params }, { activeOrganization }]) => {
        return this.integrationsService
          .installGenetec(activeOrganization.orgId, params)
          .pipe(
            switchMap(res => {
              return [
                IntegrationsActions.getIntegrations(),
                IntegrationsActions.installGenetecSuccess(),
                SharedActions.showMessage({ success: 'Genetec installed successfully' }),
              ];
            }),
            catchError(err => [SharedActions.showMessage({ error: err.error?.message || 'Failed to install Genetec' })]),
          );
      }),
      share(),
    ),
  );

  public uninstallGenetec$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.uninstallGenetec),
      withLatestFrom(this.store$.pipe(select(state => state.organizationState))),
      switchMap(([, { activeOrganization }]) => {
        return this.integrationsService
          .uninstallGenetec(activeOrganization.orgId)
          .pipe(
            switchMap(res => {
              return [
                IntegrationsActions.getIntegrations(),
                SharedActions.showMessage({ success: 'Genetec uninstalled successfully' }),
                IntegrationsActions.uninstallGenetecSuccess(),
              ];
            }),
            catchError(err => [SharedActions.showMessage({ error: err })]),
          );
      }),
      share(),
    ),
  );

  public getApiLimits$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.getApiLimits),
      withLatestFrom(this.store$.pipe(select(OrganizationSelectors.selectOrgId))),
      switchMap(([{ serviceId }, orgId]) => {
        return this.integrationsService
          .getApiLimits(orgId, serviceId)
          .pipe(
            map(res => IntegrationsActions.getApiLimitsSuccess({ apiLimits: res })),
            catchError(err => of(SharedActions.showMessage({ error: JSON.stringify(err) })))
          );
      })
    )
  );

  public getVccLogAnalytics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.getVccLogAnalytics),
      withLatestFrom(this.store$.pipe(select(OrganizationSelectors.selectOrgId))),
      switchMap(([action, orgId]) => {
        const { startTimestamp, endTimestamp } = action;
        return this.integrationsService
          .getVccLogAnalytics(orgId, startTimestamp, endTimestamp)
          .pipe(
            map(res => IntegrationsActions.getVccLogAnalyticsSuccess({ vccLogAnalytics: res })),
            catchError(err => of(IntegrationsActions.getVccLogAnalyticsFailure({ error: err })))
          );
      })
    )
  );

  public getCcLogAnalytics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.getCcLogAnalytics),
      withLatestFrom(this.store$.pipe(select(OrganizationSelectors.selectOrgId))),
      switchMap(([action, orgId]) => {
        const { startTimestamp, endTimestamp } = action;
        return this.integrationsService
          .getCcLogAnalytics(orgId, startTimestamp, endTimestamp)
          .pipe(
            map(res => IntegrationsActions.getCcLogAnalyticsSuccess({ ccLogAnalytics: res })),
            catchError(err => of(IntegrationsActions.getCcLogAnalyticsFailure({ error: err })))
          );
      })
    )
  );

  public getApiLimitsHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationsActions.getApiLimitsHistory),
      withLatestFrom(this.store$.pipe(select(OrganizationSelectors.selectOrgId))),
      switchMap(([action, orgId]) => {
        const { startTimestamp, endTimestamp } = action;
        return this.integrationsService
          .getApiLimitsHistory(orgId, startTimestamp, endTimestamp)
          .pipe(
            map(res => IntegrationsActions.getApiLimitsHistorySuccess({ apiLimitsHistory: res })),
            catchError(err => of(IntegrationsActions.getApiLimitsHistoryFailure({ error: err })))
          );
      })
    )
  );

  constructor(private actions$: Actions, private store$: Store<AppState>, private integrationsService: IntegrationsService,
  ) {
  }
}
